import { ArrowUp, Facebook, Instagram, Mail, Phone, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./Shared";
import logoMark from "@/assets/smg-logo-mark.png";

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.05z" />
    </svg>
  );
}

const SOCIALS = [
  { name: "Facebook", icon: Facebook, url: "" },
  { name: "Instagram", icon: Instagram, url: "" },
  { name: "TikTok", icon: TikTokIcon, url: "" },
  { name: "YouTube", icon: Youtube, url: "" },
];

export default function Footer() {
  return (
    <footer id="contact" className="relative overflow-hidden border-t border-white/10">
      <div className="spectrum-rule" aria-hidden="true" />
      <div
        className="absolute -bottom-40 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-volt/10 blur-[140px]"
        aria-hidden="true"
      />

      <div className="relative mx-auto w-full max-w-7xl px-6 py-24 sm:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col items-start gap-10 lg:flex-row lg:items-end lg:justify-between">
            <h2 className="font-display text-5xl uppercase leading-[1.02] sm:text-7xl lg:text-8xl">
              Get in<br />
              <span className="text-volt">the room.</span>
            </h2>
            <div className="flex flex-col gap-4 sm:flex-row">
              <a
                href="tel:+16195524677"
                data-testid="footer-call-link"
                className="group inline-flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 transition-all hover:border-volt hover:text-volt"
              >
                <Phone className="h-4 w-4 text-volt" />
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Call</span>
                  <span className="font-display text-sm uppercase tracking-wider">+1 (619) 552-4677</span>
                </span>
              </a>
              <a
                href="mailto:brian@smgmediagroup.com?cc=jacob@smgmediagroup.com"
                data-testid="footer-email-link"
                className="group inline-flex items-center gap-3 rounded-full bg-volt px-7 py-4 transition-all hover:bg-volt-light hover:shadow-[0_0_40px_rgba(132,204,22,0.3)]"
              >
                <Mail className="h-4 w-4 text-ink" />
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-ink/60">Email</span>
                  <span className="font-display text-sm uppercase tracking-wider text-ink">repme@smgsportsmgt.com</span>
                </span>
              </a>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 flex items-center gap-3">
          {SOCIALS.map((s) =>
            s.url ? (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-${s.name.toLowerCase()}-link`}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-300 transition-all hover:border-volt hover:text-volt"
              >
                <s.icon className="h-5 w-5" />
              </a>
            ) : (
              <button
                key={s.name}
                onClick={() => toast.success(`${s.name} coming soon — follow us shortly.`)}
                data-testid={`social-${s.name.toLowerCase()}-placeholder`}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-slate-400 transition-all hover:border-volt hover:text-volt"
                aria-label={`${s.name} (coming soon)`}
              >
                <s.icon className="h-5 w-5" />
              </button>
            )
          )}
        </div>

        <div className="mt-16 flex flex-col gap-8 border-t border-white/10 pt-10 md:flex-row md:items-center md:justify-between">
          <div className="flex shrink-0 items-center gap-4">
            <span className="relative inline-block">
              <img
                src={logoMark}
                alt="SMG Sports Management Group International"
                className="h-14 w-auto max-w-none"
                data-testid="footer-logo"
              />
              <span
                className="absolute -right-3 -top-1 font-mono text-[10px] text-slate-400"
                data-testid="footer-logo-tm"
              >
                ™
              </span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
            <span>Carlsbad, CA</span>
            <span className="h-1 w-1 rounded-full bg-volt" />
            <a href="https://smgmediagroup.com/" target="_blank" rel="noopener noreferrer" data-testid="footer-media-group-link" className="transition-colors hover:text-volt">
              SMG Media Group
            </a>
            <a href="/guides" data-testid="footer-guides-link" className="transition-colors hover:text-volt">
              Guides
            </a>
            <a href="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-volt">
              Privacy Policy
            </a>
            <a href="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-volt">
              Terms of Use
            </a>
            <a href="/accessibility" data-testid="footer-accessibility-link" className="transition-colors hover:text-volt">
              Accessibility
            </a>
            <a href="/social-responsibility" data-testid="footer-social-responsibility-link" className="transition-colors hover:text-volt">
              Social Responsibility
            </a>
            <a href="/human-rights" data-testid="footer-human-rights-link" className="transition-colors hover:text-volt">
              Human Rights
            </a>
            <a href="/us-privacy-notice" data-testid="footer-us-privacy-notice-link" className="transition-colors hover:text-volt">
              US Client Privacy Notice
            </a>
            <a href="/client-privacy-notice" data-testid="footer-intl-privacy-notice-link" className="transition-colors hover:text-volt">
              Client Privacy Notice (excluding US)
            </a>
            <button
              onClick={() => window.dispatchEvent(new Event("smg-cookie-reset"))}
              data-testid="footer-cookie-settings-btn"
              className="uppercase transition-colors hover:text-volt"
            >
              Cookie Settings
            </button>
            <span className="h-1 w-1 rounded-full bg-volt" />
            <a href="/admin" data-testid="footer-admin-link" className="transition-colors hover:text-volt">
              Admin
            </a>
          </div>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="back-to-top-btn"
            className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/20 text-slate-300 transition-all hover:border-volt hover:text-volt"
            aria-label="Back to top"
          >
            <ArrowUp className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-10 space-y-3 text-[11px] leading-relaxed text-slate-500">
          <p>
            SMG Sports Management Group International is a division of SMG Media
            Group. Representation subject to approval. SMG makes no guarantee of
            sponsorships, earnings, scholarships, rankings or professional
            status. Youth-athlete inquiries require a parent or legal guardian.
          </p>
          <p>© 2026 SMG Media Group. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
